import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Fuel, Zap, Sparkles, ArrowRight, Bell } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Reusable "fuel gauge" — animated SVG meter showing tank fill level.
 * Lives at the heart of the Community Tank visual language.
 */
export function FuelGauge({ progress = 0, size = 220, label }) {
  const safe = Math.max(0, Math.min(100, Number(progress) || 0));
  // Half-donut sweep: left tip → over the top → right tip.
  // SVG +y is down, so we negate sin to draw upward.
  const cx = size / 2;
  const r = size * 0.4;
  const strokeWidth = size * 0.085;
  // We need vertical room for the arc (r above cy) + stroke + a little padding.
  const padTop = strokeWidth;
  const padBottom = strokeWidth * 0.6;
  const cy = padTop + r;
  const svgHeight = cy + padBottom;
  const startX = cx - r, startY = cy;
  const endX = cx + r, endY = cy;
  const fillT = safe / 100;
  const fillAngle = Math.PI * (1 - fillT);   // π (left) → 0 (right)
  const fillX = cx + r * Math.cos(fillAngle);
  const fillY = cy - r * Math.sin(fillAngle);
  const largeArc = fillT > 0.5 ? 1 : 0;
  const needleR = r - strokeWidth * 0.6;
  const needleX = cx + needleR * Math.cos(fillAngle);
  const needleY = cy - needleR * Math.sin(fillAngle);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => {
    const a = Math.PI * (1 - t);
    return {
      x1: cx + (r - strokeWidth * 0.4) * Math.cos(a),
      y1: cy - (r - strokeWidth * 0.4) * Math.sin(a),
      x2: cx + (r + strokeWidth * 0.18) * Math.cos(a),
      y2: cy - (r + strokeWidth * 0.18) * Math.sin(a),
    };
  });

  return (
    <div className="inline-block text-center" data-testid="fuel-gauge">
      <svg
        width="100%"
        height={svgHeight}
        viewBox={`0 0 ${size} ${svgHeight}`}
        style={{ maxWidth: size, display: "block", margin: "0 auto" }}
      >
        <path
          d={`M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`}
          fill="none" stroke="rgba(255,255,255,0.10)"
          strokeWidth={strokeWidth} strokeLinecap="round"
        />
        {fillT > 0 && (
          <path
            d={`M ${startX} ${startY} A ${r} ${r} 0 ${largeArc} 1 ${fillX} ${fillY}`}
            fill="none" stroke="url(#fuel-grad)"
            strokeWidth={strokeWidth} strokeLinecap="round"
            style={{ transition: "all 0.9s cubic-bezier(0.4, 0, 0.2, 1)" }}
          />
        )}
        {ticks.map((t, i) => (
          <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke="rgba(13,16,13,0.35)" strokeWidth={1.5} strokeLinecap="round" />
        ))}
        <line
          x1={cx} y1={cy} x2={needleX} y2={needleY}
          stroke="#0d100d" strokeWidth={3} strokeLinecap="round"
          style={{ transition: "all 0.9s cubic-bezier(0.4, 0, 0.2, 1)" }}
        />
        <circle cx={cx} cy={cy} r={size * 0.045} fill="#0d100d" stroke="#76B82A" strokeWidth={2} />
        <defs>
          <linearGradient id="fuel-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#76B82A" />
            <stop offset="100%" stopColor="#d7a62a" />
          </linearGradient>
        </defs>
      </svg>
      <div className="mt-2">
        <div className="splash-display text-3xl md:text-4xl leading-none">{safe}%</div>
        {label && (
          <div className="splash-mono uppercase tracking-widest text-[10px] mt-1 opacity-65">
            {label}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Tier pill — color-coded label for IGNITER / BOOSTER / SPARK.
 */
export function TierPill({ tier }) {
  const map = {
    igniter: { label: "Igniter", color: "#76B82A", icon: Zap },
    booster: { label: "Booster", color: "#d7a62a", icon: Sparkles },
    spark:   { label: "Spark",   color: "#f7f3e6", icon: Sparkles },
  };
  const t = map[tier] || map.spark;
  const I = t.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 splash-mono uppercase tracking-widest text-[10px] border-2"
      style={{ color: t.color, borderColor: t.color }}
      data-testid={`tier-pill-${tier}`}
    >
      <I size={10} strokeWidth={2.5} />
      {t.label}
    </span>
  );
}

/**
 * "How to Fuel" instructions modal. All donations are offline (Venmo /
 * CashApp / Cash) so we don't open Stripe — just guide the donor through
 * sending the money via their preferred channel and DMing their handle.
 */
export function FuelInstructionsModal({ open, onClose, settings }) {
  if (!open || !settings) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/75 backdrop-blur-sm p-0 md:p-4"
      onClick={onClose}
      data-testid="fuel-instructions-modal"
    >
      <div
        className="splash-root bg-[#0d100d] border-2 border-[#76B82A]/40 max-w-md w-full max-h-[90vh] overflow-y-auto p-6 md:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="splash-mono uppercase tracking-[0.22em] text-[10px] text-[#76B82A]">
          Fuel the Tank
        </div>
        <h3 className="splash-display text-3xl md:text-4xl uppercase mt-1 text-[#fffaef]">
          Send any amount.
        </h3>
        <p className="mt-3 text-[14px] text-[#f7f3e6]/75 leading-relaxed">
          Every dollar fuels the next People's Drop. Use whatever's easiest:
        </p>
        <div className="mt-5 space-y-3">
          <div className="bg-[#fffaef] border-2 border-[#0d100d] p-4" data-testid="fuel-venmo">
            <div className="splash-mono uppercase tracking-widest text-[10px] text-[#76B82A]">Venmo</div>
            <div className="splash-display text-xl md:text-2xl text-[#0d100d] mt-1">
              {settings.venmo_handle || "@TheLocalLoot"}
            </div>
          </div>
          <div className="bg-[#fffaef] border-2 border-[#0d100d] p-4" data-testid="fuel-cashapp">
            <div className="splash-mono uppercase tracking-widest text-[10px] text-[#76B82A]">Cash App</div>
            <div className="splash-display text-xl md:text-2xl text-[#0d100d] mt-1">
              {settings.cashapp_handle || "$TheLocalLoot"}
            </div>
          </div>
          <div className="bg-[#fffaef] border-2 border-[#0d100d] p-4" data-testid="fuel-cash">
            <div className="splash-mono uppercase tracking-widest text-[10px] text-[#76B82A]">Cash</div>
            <div className="text-[14px] text-[#0d100d] mt-1">
              {settings.cash_instructions || "Text or DM us to arrange a hand-off."}
            </div>
          </div>
        </div>
        <div className="mt-5 bg-[#76B82A]/10 border-l-4 border-[#76B82A] p-3 text-[13px] text-[#f7f3e6]/85">
          {settings.offline_followup || "After you send, DM us your @username on social so we credit you to the Wall of Igniters."}
        </div>
        {settings.disclaimer && (
          <p className="mt-5 text-[10px] uppercase tracking-widest text-[#f7f3e6]/35 leading-relaxed">
            {settings.disclaimer}
          </p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="btn-loot-solid w-full justify-center mt-5"
          data-testid="fuel-instructions-close"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

/**
 * Splash / Home embeddable section. Compact tank summary + CTA to /community-tank
 * full page. Renders nothing if the tank is disabled. Polls /api/tank/status
 * once on mount (lightweight — small payload).
 */
export default function CommunityTankSection({ variant = "cream" }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/tank/status")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setHidden(true); });
    return () => { cancelled = true; };
  }, []);

  if (hidden || !data || !data.enabled) return null;
  const s = data.settings || {};
  const t = data.tank || {};
  const latest = data.latest_fuel;

  const onCream = variant === "cream";
  const sectionClass = onCream
    ? "cream-section py-14 md:py-20"
    : "bg-[#0d100d] border-y border-[#76B82A]/15 py-14 md:py-20";
  const overlineColor = "text-[#d7a62a]";
  const headlineColor = onCream ? "text-[#0d100d]" : "text-[#fffaef]";
  const bodyColor = onCream ? "text-[#0d100d]/75" : "text-[#f7f3e6]/75";

  return (
    <section className={`${sectionClass} relative overflow-hidden`} data-testid="community-tank-section">
      {/* Subtle accent stripe down the left edge so the section reads as
          a "punched-out" feature panel rather than just another cream block. */}
      <div
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1.5 md:w-2"
        style={{ background: "linear-gradient(180deg, #76B82A 0%, #d7a62a 100%)" }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* LEFT — copy + CTAs */}
          <div className="lg:col-span-7">
            <div className={`splash-mono uppercase tracking-[0.22em] text-[11px] ${overlineColor}`}>
              {s.splash_overline || "This game runs on you."}
            </div>
            <h2 className={`splash-display text-4xl md:text-6xl uppercase mt-2 leading-[0.95] ${headlineColor}`}>
              <span className="inline-flex items-center gap-3 flex-wrap">
                <Fuel size={36} className="text-[#76B82A] inline shrink-0" />
                Community LootFund
              </span>
            </h2>
            <div className={`mt-3 splash-display text-xl md:text-2xl uppercase ${onCream ? "text-[#76B82A]" : "text-[#76B82A]"}`}>
              {s.splash_headline || "Fuel the tank."} <span className="opacity-60">Fund the next People's Drop.</span>
            </div>
            <p className={`mt-4 max-w-xl text-[14px] md:text-[15px] ${bodyColor} leading-relaxed`}>
              {s.splash_body || "Every dollar goes directly into the prize cards and the local shops we buy them from."}
            </p>
            {latest && (
              <div
                className={`mt-5 inline-flex items-center gap-2 px-3 py-1.5 splash-mono uppercase tracking-widest text-[11px] border-2 ${onCream ? "bg-[#0d100d] border-[#0d100d] text-[#76B82A]" : "bg-[#76B82A]/10 border-[#76B82A] text-[#76B82A]"}`}
                data-testid="community-tank-latest-fuel"
              >
                <Zap size={12} fill="#76B82A" /> Latest fuel: {latest.display_name} · {latest.amount_label} · {latest.tier}
              </div>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="btn-loot-solid"
                data-testid="community-tank-fuel-cta"
              >
                <Fuel size={16} /> {s.splash_cta_label || "Fuel the Tank"}
              </button>
              <Link
                to="/community-tank"
                className={`inline-flex items-center gap-2 px-5 py-2.5 border-2 splash-mono uppercase tracking-[0.18em] text-xs transition ${onCream ? "border-[#0d100d] text-[#0d100d] hover:bg-[#0d100d] hover:text-[#76B82A]" : "border-[#fffaef] text-[#fffaef] hover:bg-[#fffaef] hover:text-[#0d100d]"}`}
                data-testid="community-tank-learn-more"
              >
                See the wall <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* RIGHT — compact tank gauge card. Always dark + glowing
              shadow so it pops on either cream or dark variants. */}
          <div className="lg:col-span-5">
            <article
              className="bg-[#0d100d] text-[#fffaef] border-2 border-[#0d100d] shadow-[10px_10px_0_rgba(118,184,42,0.7)] p-5 md:p-7 text-center relative"
              data-testid="community-tank-gauge-card"
            >
              {/* Pulsing corner star to hint live energy */}
              <div className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-[#0d100d] border-2 border-[#76B82A] flex items-center justify-center shadow-[0_0_14px_rgba(118,184,42,0.7)] animate-pulse-slow">
                <Zap size={14} fill="#76B82A" stroke="#76B82A" />
              </div>
              <div className="splash-mono uppercase tracking-[0.22em] text-[10px] text-[#76B82A]">
                Current tank level
              </div>
              <div className="splash-display text-5xl md:text-6xl mt-1 leading-none text-[#fffaef]">
                {t.available_label}
              </div>
              <div className="splash-mono text-[11px] opacity-65 mt-1">
                of {t.target_label} target
              </div>
              <div className="mt-4">
                <FuelGauge progress={t.progress_pct} size={240} label="To next People's Drop" />
              </div>
              <div className="mt-2 splash-mono text-[10px] uppercase tracking-widest opacity-65">
                {t.donor_count} hunter{t.donor_count === 1 ? "" : "s"} fueled
              </div>
            </article>
          </div>
        </div>
      </div>
      <FuelInstructionsModal open={open} onClose={() => setOpen(false)} settings={s} />
    </section>
  );
}

/* "Coming soon" placeholder shown when admin disables the tank. */
export function ComingSoonPanel({ data }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tank/notify-me", { email });
      setDone(true);
      toast.success("We'll let you know.");
    } catch {
      toast.error("Couldn't sign up. Try again in a bit.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-[#0d100d] py-20 md:py-28 min-h-[70vh] flex items-center" data-testid="tank-comingsoon">
      <div className="max-w-xl mx-auto px-4 md:px-8 text-center">
        <Fuel size={48} className="mx-auto text-[#76B82A]" />
        <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a] mt-5">
          Loading the pump
        </div>
        <h1 className="splash-display text-4xl md:text-6xl uppercase mt-3 text-[#fffaef] leading-[0.95]">
          {data?.comingsoon_headline || "The Community Tank is loading."}
        </h1>
        <p className="mt-4 text-[15px] text-[#f7f3e6]/75 leading-relaxed">
          {data?.comingsoon_body || "We're getting it ready. Drop your email and we'll let you know the second the fuel pump opens."}
        </p>
        {done ? (
          <div className="mt-6 splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A]">
            ✓ You're on the list.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@somewhere.com"
              className="lm-input flex-1 max-w-xs mx-auto sm:mx-0"
              data-testid="tank-comingsoon-email"
            />
            <button
              type="submit" disabled={busy}
              className="btn-loot-solid justify-center"
              data-testid="tank-comingsoon-submit"
            >
              <Bell size={14} /> Notify me
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
